import { useState } from "react";
import { Row, Col, Dropdown } from "react-bootstrap";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import NewModal from "../../../shared/Modals/NewModal";
import JobVideoEmbed from "./JobVideoEmbed";
import JobPostFiles from "./JobPostFiles";
import { platformOf } from "./jobMedia";
import {
  jobExternalId,
  jobDuration,
  jobUploader,
  jobAssetsCount,
  jobSourceRef,
  jobError,
  jobCreatedAt,
  jobUpdatedAt,
  jobTranscript,
  jobScripts,
  jobArticle,
} from "./jobFields";
import {
  formatDuration,
  nextActionFor,
  jobTitle,
  instructionList,
} from "./jobStatus";

const Field = ({ label, children }) => {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className="mb-2">
      <div className="text-muted font-size-11 text-uppercase">{label}</div>
      <div>{children}</div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h5 className="font-size-14 mb-3">{title}</h5>
    {children}
  </div>
);

/**
 * Mini-dashboard for one job. Everything here is a read-only mirror of what the
 * Content Machine reported — the bridge overwrites `raw` on every push, so this
 * view never edits it.
 */
const JobDetailModal = ({
  show,
  setShow,
  job,
  onOpenPost,
  onOpenTeleprompter,
  onOpenGroup,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const loadDetail = async () => {
    setDetail(null);
    setShowRaw(false);
    if (!job?.id) return;
    setLoading(true);
    const data = await crudApi.show(SocialEndpoints.jobs.show, job.id);
    // Tolerate both a bare job and a { job: ... } wrapper.
    setDetail(data ? data.job ?? data : null);
    setLoading(false);
  };

  // Fall back to the board's copy while the detail request is in flight.
  const current = detail ?? job;
  const title = current ? jobTitle(current) : { text: "Job", muted: false };
  const nextAction = current ? nextActionFor(current) : null;
  const duration = formatDuration(jobDuration(current));
  const uploader = jobUploader(current);
  const scripts = jobScripts(current);
  const transcript = jobTranscript(current);
  const article = jobArticle(current);
  const instructions = current ? instructionList(current) : [];
  // download.platform, never source.platform — that one says "telegram".
  const platform = current ? platformOf(current) : null;

  return (
    <NewModal
      show={show}
      setShow={setShow}
      title={title.text}
      onShow={loadDetail}
      fullscreen={false}
      size="lg"
    >
      {loading && <div className="text-muted mb-3">Loading job…</div>}

      {current && (
        <>
          {current.status === "error" && jobError(current) && (
            <div className="alert alert-danger">{jobError(current)}</div>
          )}

          <Section title="Status">
            <Row>
              <Col md={4}>
                <Field label="Status">{current.status}</Field>
              </Col>
              <Col md={4}>
                <Field label="Next">{nextAction?.label}</Field>
              </Col>
              <Col md={4}>
                <Field label="Job id">{jobExternalId(current)}</Field>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Field label="Created">{jobCreatedAt(current)}</Field>
              </Col>
              <Col md={4}>
                <Field label="Updated">{jobUpdatedAt(current)}</Field>
              </Col>
            </Row>
          </Section>

          <Row>
            <Col md={6}>
              <Section title="Source">
                <JobVideoEmbed job={current} />
                <Field label="Link">
                  {current.link ? (
                    <a
                      href={current.link}
                      target="_blank"
                      rel="noreferrer"
                      style={{ wordBreak: "break-all" }}
                    >
                      {current.link}
                    </a>
                  ) : null}
                </Field>
                <Row>
                  <Col md={6}>
                    <Field label="Platform">{platform}</Field>
                  </Col>
                  <Col md={6}>
                    <Field label="Kind">{current.link_type}</Field>
                  </Col>
                  <Col md={6}>
                    <Field label="Type">{current.type}</Field>
                  </Col>
                  <Col md={6}>
                    <Field label="Brand">{current.brand}</Field>
                  </Col>
                  <Col md={6}>
                    <Field label="Duration">{duration}</Field>
                  </Col>
                  <Col md={6}>
                    <Field label="Uploader">{uploader}</Field>
                  </Col>
                  <Col md={6}>
                    <Field label="Assets">{jobAssetsCount(current)}</Field>
                  </Col>
                  <Col md={6}>
                    <Field label="Source ref">
                      <code className="font-size-11">
                        {jobSourceRef(current)}
                      </code>
                    </Field>
                  </Col>
                </Row>

                {article && (
                  <div className="border-top pt-2 mt-2">
                    <div className="text-muted font-size-11 text-uppercase mb-2">
                      Extracted article
                    </div>
                    <Field label="Headline">{article.title}</Field>
                    <Row>
                      <Col md={6}>
                        <Field label="Author">{article.author}</Field>
                      </Col>
                      <Col md={6}>
                        <Field label="Publication">{article.sitename}</Field>
                      </Col>
                      <Col md={6}>
                        <Field label="Date">{article.date}</Field>
                      </Col>
                      <Col md={6}>
                        <Field label="Words">{article.wordCount}</Field>
                      </Col>
                    </Row>
                    {article.url && (
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ wordBreak: "break-all" }}
                      >
                        {article.url}
                      </a>
                    )}
                  </div>
                )}
              </Section>
            </Col>

            <Col md={6}>
              <Section title={`Instructions (${instructions.length})`}>
                {instructions.length === 0 ? (
                  <div className="text-muted font-size-12">
                    No instructions on this job.
                  </div>
                ) : (
                  <ol className="ps-3 mb-0">
                    {instructions.map((entry, index) => (
                      <li key={index} className="mb-2">
                        {entry.voice && (
                          <span className="badge me-2 rounded-pill badge-info-subtle">
                            voice
                          </span>
                        )}
                        <span style={{ whiteSpace: "pre-wrap", fontSize: "13px" }}>
                          {entry.text}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </Section>
            </Col>
          </Row>

          {transcript && (
            <Section title="Transcript">
              {transcript.text ? (
                <div
                  className="text-muted"
                  style={{ whiteSpace: "pre-wrap", fontSize: "13px" }}
                >
                  {transcript.text}
                </div>
              ) : (
                <div className="text-muted font-size-12">
                  {transcript.chars
                    ? `${transcript.chars} characters transcribed`
                    : "Transcribed"}
                  {transcript.file && (
                    <>
                      {" — "}
                      <code className="font-size-11">{transcript.file}</code>
                    </>
                  )}
                  . The text stays on the machine; only an excerpt is mirrored
                  here, and this job has none.
                </div>
              )}
            </Section>
          )}

          <Section title="Scripts">
            {scripts.list.length === 0 ? (
              <div className="text-muted font-size-12">
                {scripts.mirrored
                  ? "No scripts generated yet."
                  : current.status === "scripted"
                    ? "This job is scripted, but the payload carries no script list — the machine did not mirror it."
                    : "No scripts generated yet."}
              </div>
            ) : (
              <ul className="list-unstyled mb-0">
                {scripts.list.map((script, index) => (
                  <li key={`${script.file ?? index}`} className="mb-1">
                    <span className="badge me-2 rounded-pill badge-warning-subtle">
                      {script.type ?? "script"}
                    </span>
                    <code className="font-size-11">{script.file}</code>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-muted font-size-11 mt-2">
              Script files live on the machine. The script text itself is in the
              linked post.
            </div>
          </Section>

          <Section title="Linked">
            <Row>
              <Col md={6}>
                <Field label="Post">
                  {current.post_id ? (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="link"
                        size="sm"
                        className="p-0 align-baseline text-decoration-none"
                      >
                        Post #{current.post_id}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => onOpenPost(current.post_id)}>
                          Open post
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => onOpenTeleprompter(current.post_id)}
                        >
                          Teleprompter
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    "—"
                  )}
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Group">
                  {current.group_id ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={() => onOpenGroup(current.group_id)}
                    >
                      Go to group #{current.group_id}
                    </button>
                  ) : (
                    "—"
                  )}
                </Field>
              </Col>
            </Row>

            {current.post_id && (
              <div className="mt-2">
                <div className="text-muted font-size-11 text-uppercase mb-1">
                  Files on the post
                </div>
                <JobPostFiles postId={current.post_id} />
              </div>
            )}
          </Section>

          {current.raw && (
            <div>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setShowRaw(!showRaw)}
              >
                {showRaw ? "Hide" : "Show"} raw job.json
              </button>
              {showRaw && (
                <pre
                  className="mt-2 p-3 bg-light"
                  style={{ fontSize: "11px", maxHeight: "320px", overflow: "auto" }}
                >
                  {JSON.stringify(current.raw, null, 2)}
                </pre>
              )}
            </div>
          )}
        </>
      )}
    </NewModal>
  );
};

export default JobDetailModal;
